import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  PagedDataGrid,
  FilterType,
  FilterOperator,
  buildMetadataFilterColumns,
  StatusBadge,
  type IPagedDataGridRefType,
  type DataGridColumn,
  type ICommandBarItem,
  type IFilterableColumn,
} from '@moko/ui';
import { FontIcon, Link } from '@fluentui/react';
import { ClipboardTask24Regular } from '@fluentui/react-icons';
import { getMetadata, IField, ILookup } from '../../services/metadataService';
import { getOrders, IOrder } from '../../services/orderService';
import {
  getOrderStatuses,
  type IOrderStatus,
} from '../../services/workService';
import { useAtomValue, useSetAtom } from 'jotai';
import { useNavigate } from 'react-router-dom';
import { profileDataAtom } from '../../atoms/authAtoms';
import {
  clearMessageAtom,
  errorMessageAtom,
  isInProgressAtom,
} from '../../atoms/messageBarAtoms';
import { appRoles } from '../../authConfig';
import AppContext from '../AppContext';

type IOrderItem = IOrder & {
  createdAt?: string;
  [key: string]: any;
};

const Orders = () => {
  const profileData = useAtomValue(profileDataAtom);
  const navigate = useNavigate();

  const [selectedOrderId, setSelectedOrderId] = useState<number>();
  const pagedDataGridRef = useRef<IPagedDataGridRefType>(null);

  const context = useContext(AppContext);
  const setErrorMessage = useSetAtom(errorMessageAtom);
  const setIsInProgress = useSetAtom(isInProgressAtom);
  const clearMessage = useSetAtom(clearMessageAtom);

  const [metadataFields, setMetadataFields] = useState<IField[]>([]);
  const [metadataLookups, setMetadataLookups] = useState<ILookup[]>([]);
  const [orderStatuses, setOrderStatuses] = useState<IOrderStatus[]>([]);

  useEffect(() => {
    const abortController = new AbortController();
    getOrderStatuses(abortController)
      .then((statuses) => setOrderStatuses(statuses ?? []))
      .catch(() => {});
    return () => abortController.abort();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      const abortController = new AbortController();
      setIsInProgress(true);

      try {
        const metadata = await getMetadata(abortController, 'Order');
        setMetadataFields(metadata?.fields ?? []);
        setMetadataLookups(metadata?.lookups ?? []);

        clearMessage();
        context.setSelectedTab('Orders');
      } catch (error: any) {
        setErrorMessage(error.message);
      } finally {
        setIsInProgress(false);
      }
      return () => {
        abortController.abort();
      };
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const otherInfoColumns = useMemo<DataGridColumn<IOrderItem>[]>(
    () =>
      metadataFields
        .filter((f) => f.showInGrid)
        .map((f) => ({
          key: f.name,
          name: f.name,
          width: 100,
          type:
            f.fieldType === 'Date'
              ? ('date' as const)
              : f.fieldType === 'DateTime'
              ? ('datetime' as const)
              : undefined,
          ...(f.fieldType === 'Image' && {
            renderCell: (item: IOrderItem) => (
              <img src={(item as any)[f.name]} style={{ width: 20 }} alt="" />
            ),
          }),
        })),
    [metadataFields]
  );

  const otherInfoFieldNames = useMemo(
    () => otherInfoColumns.map((c) => c.name ?? ''),
    [otherInfoColumns]
  );

  const customFilterColumns = useMemo(
    () => buildMetadataFilterColumns(metadataFields, metadataLookups),
    [metadataFields, metadataLookups]
  );

  const orderStatusOptions = useMemo(
    () =>
      orderStatuses.map((s) => ({
        value: String(s.id),
        label: s.name,
      })),
    [orderStatuses]
  );

  const baseColumns: DataGridColumn<IOrderItem>[] = [
    {
      key: 'id',
      name: 'Id',
      width: 50,
      renderCell: (item: IOrderItem) => (
        <Link onClick={() => navigate(`/editOrder?id=${item.id}`)}>
          {item.id}
        </Link>
      ),
    },
    {
      key: 'code',
      name: 'Code',
      width: 100,
    },
    {
      key: 'lastWorkId',
      name: 'Work Id',
      width: 100,
      renderCell: (item: IOrderItem) =>
        item.lastWorkId ? (
          <Link onClick={() => navigate(`/editWork?id=${item.lastWorkId}`)}>
            {item.lastWorkId}
          </Link>
        ) : (
          <></>
        ),
    },
    {
      key: 'isTemplate',
      name: 'Is Template',
      width: 100,
    },
    {
      key: 'description',
      name: 'Description',
      width: 250,
    },
    {
      key: 'statusId',
      name: 'Status',
      width: 120,
      renderCell: (item: IOrderItem) => (
        <StatusBadge statuses={orderStatuses} statusId={item.statusId} />
      ),
    },
    {
      key: 'createdAt',
      name: 'Created at',
      width: 155,
      type: 'datetime',
    },
  ];

  const columns = useMemo(
    () => [...baseColumns, ...otherInfoColumns],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [otherInfoColumns]
  );

  const filterColumns: IFilterableColumn[] = useMemo(
    () => [
      { key: 'id', label: 'Id', type: FilterType.String },
      { key: 'code', label: 'Code', type: FilterType.String },
      { key: 'description', label: 'Description', type: FilterType.String },
      {
        key: 'statusId',
        label: 'Status',
        type: FilterType.Number,
        operators: [
          FilterOperator.Eq,
          FilterOperator.NEq,
          FilterOperator.NNul,
          FilterOperator.In,
        ],
        options: orderStatusOptions,
      },
      { key: 'createdAt', label: 'Created at', type: FilterType.Date },
      {
        key: 'isCompleted',
        label: 'Is Completed',
        type: FilterType.Number,
        toggleOptions: [{ value: '1' }, { value: '0' }],
      },
      ...customFilterColumns,
    ],
    [orderStatusOptions, customFilterColumns]
  );

  const commandBarItems: ICommandBarItem[] = useMemo(
    () => [
      {
        key: 'refresh',
        text: 'Refresh',
        icon: <FontIcon iconName="Refresh" />,
        onClick: (ctx) => ctx.refresh(),
      },
      {
        key: 'newOrder',
        text: 'New',
        icon: <FontIcon iconName="Add" />,
        onClick: () => navigate('/newOrder'),
        disabled: !profileData.roles.includes(appRoles.Admin),
      },
      {
        key: 'editOrder',
        text: 'Edit',
        icon: <FontIcon iconName="Edit" />,
        onClick: () => navigate(`/editOrder?id=${selectedOrderId}`),
        disabled:
          !profileData.roles.includes(appRoles.Admin) || !selectedOrderId,
      },
    ],
    [profileData.roles, selectedOrderId, navigate]
  );

  return (
    <div style={{ maxWidth: 1000 }}>
      <PagedDataGrid<IOrderItem>
        ref={pagedDataGridRef}
        columns={columns}
        filterEnabled
        filterColumns={filterColumns}
        getAction={async (
          abortController,
          pageSize,
          pageNo,
          orderBy,
          search,
          filters
        ) => {
          const data = await getOrders(
            abortController,
            pageSize,
            pageNo,
            orderBy,
            search,
            filters
          );
          return {
            ...data,
            items: data.items.map((order) => ({
              ...order,
            })) as IOrderItem[],
          };
        }}
        commandBarItems={commandBarItems}
        selectionMode="single"
        onSelectionChange={(selection) => {
          setSelectedOrderId(Number(selection.id));
        }}
        onRowDoubleClicked={(selection) => {
          navigate(`/editOrder?id=${selection.id}`);
        }}
        listHeigh={525}
        loadingConfig={{
          message: 'Loading orders...',
          icon: (
            <ClipboardTask24Regular
              style={{ width: 125, height: 125, color: 'lightgray' }}
            />
          ),
        }}
        otherInfoFieldNames={otherInfoFieldNames}
      />
    </div>
  );
};

export default Orders;
